/** Where the app runs: installed or in a tab, iOS / Android / desktop, a wallet's in-app browser, Push API available. */
export interface PwaEnv {
  standalone: boolean
  ios: boolean
  iosVersion: number | null
  android: boolean
  mobile: boolean
  /** a wallet's or a social app's built-in browser: no "Add to Home Screen" there */
  inApp: boolean
  /** service worker + PushManager + Notification all present */
  pushApi: boolean
}

const IN_APP = /MetaMaskMobile|TrustWallet|Trust\/|TokenPocket|OKApp|OKEx|BitKeep|Bitget|CoinbaseWallet|imToken|SafePal|Binance|FBAN|FBAV|Instagram|Telegram|Line\/|MicroMessenger|; wv\)/i

export function isStandalone(win: Window): boolean {
  try {
    if (win.matchMedia?.('(display-mode: standalone)')?.matches) return true
    if (win.matchMedia?.('(display-mode: fullscreen)')?.matches) return true
  } catch {
    /* old browsers */
  }
  return (win.navigator as Navigator & { standalone?: boolean }).standalone === true
}

export function detectEnv(win: Window): PwaEnv {
  const nav = win.navigator
  const ua = nav.userAgent || ''
  const ipadOs = nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1
  const ios = /iPhone|iPad|iPod/.test(ua) || ipadOs
  const m = ua.match(/OS (\d+)[_.](\d+)/)
  const iosVersion = ios && m ? Number(m[1]) + Number(m[2]) / 100 : ios && ipadOs ? 17 : null
  const android = /Android/i.test(ua)
  return {
    standalone: isStandalone(win),
    ios,
    iosVersion,
    android,
    mobile: ios || android || /Mobile/i.test(ua),
    inApp: IN_APP.test(ua),
    pushApi: 'serviceWorker' in nav && 'PushManager' in win && 'Notification' in win,
  }
}

export type PushSupport = 'ok' | 'ios-install-first' | 'ios-too-old' | 'unsupported'

/** iOS has web push only in an installed (home screen) web app, from iOS 16.4. */
export function pushSupport(env: PwaEnv): PushSupport {
  if (env.ios) {
    if (env.iosVersion !== null && env.iosVersion < 16.04) return 'ios-too-old'
    if (!env.standalone) return 'ios-install-first'
  }
  return env.pushApi ? 'ok' : 'unsupported'
}
